<template>
  <section class="ma-4 ma-md-8">
    <t-page-title title="Cortesias" :on-add="() => $router.push({ name: 'create-complimentary-ticket' })" />
    <v-row justify="center" align="center">
      <v-col>
        <data-table
          ref="datatable"
          :headers="headers"
          :provider="fetch"
          :filters="filters"
          :resource="resource"
          :actions="actions"
          expand-on-click
        >
          <template #[`item.id`]="{ item }">
            <span> {{ item.id.split('-')[0] }}</span>
          </template>
          <template #[`item.created_at`]="{ item }">
            <span> {{ $formatters.date.fromSQL(item.created_at) }}</span>
          </template>
          <template #[`item.event`]="{ item }">
            <span> {{ item.event_name }}</span>
          </template>
          <template #[`item.owner`]="{ item }">
            <span v-if="item.owner">
              <b>{{ item.owner.name }}</b>
              <div>
                <small> {{ $formatters.cpf(item.owner.document) }} </small>
              </div>
            </span>
            <span v-else> -- </span>
          </template>
          <template #[`item.type`]="{ item }">
            {{ item.items?.[0].type }}
          </template>
          <template #[`item.courtesy_name`]="{ item }">
            {{ item.items?.[0].courtesy_name ?? '--' }}
          </template>
          <template #[`item.quantity`]="{ item }">
            <span> {{ item.total_items }}</span>
          </template>
          <template #expanded-row="{ item }">
            <tr v-for="(items, sectionId) in groupBy<OrderItem, string>(item.items, i => i.section_id)" :key="sectionId" style="background-color: #f5f5f5">
              <td colspan="7">
                {{ items[0].sector }} ({{ items[0].section }})
              </td>
              <td colspan="2">
                {{ items.length }}
              </td>
            </tr>
          </template>
        </data-table>
      </v-col>
    </v-row>
  </section>
</template>

<script lang="ts" setup>
import DataTable, { type DataTableType } from '@/components/datatable/DataTable.vue'
import TPageTitle from '@/components/TPageTitle.vue'
import type { DefaultDataTableActions } from '@/types/DataTable'
import ComplimentaryTicketService from '@/services/ComplimentaryTicketService'
import type Order from '@/types/Order'
import { Resources } from '@/plugins/permissions/typing'
import { useAbility } from '@casl/vue'
import { useSnackBar } from '@/stores/snackbar'
import { useDialog } from '@/stores/dialog'
import { useLoader } from '@/stores/loader'
import { DateTime, Interval } from 'luxon'
import { computed, ref, useTemplateRef } from 'vue'
import { useEventFilter } from '@/helpers/reports/eventFilter'
import { useProducerStore } from '@/stores/producer'
import { useTextFilter } from '@/helpers/reports/textFilter'
import OrderItem from '@/types/OrderItem'
import { groupBy } from '@/helpers/array'
import { useSelectFilter } from '@/helpers/reports/selectFilter'
import { useAdminStore } from '@/stores/admin'

// NOTE(allan): why there's Gratuidade stuff in here?
//
// TL;DR: it was requested a way of easing the process of identifying and canceling Gratuidade
// affected by errors (usually printing errors) on POS. This is temporary and only available
// for master users.
//
// When the POS printer fails to the point of making the tickets unreadable, the associated
// order must be canceled. The problem is that, currently, the POS app only provides the order
// number as an identifiable information, which is also commonly affected by printing errors.
//
// As a result, the responsible person relies on heuristics to identify the order, like
// checking the latest one available on the orders report as soon as the error happens. This,
// unfortunately, isn't always viable and is highly susceptible to human error.
//
// So until the new version of the POS app is released, the solution is to fetch the Gratuidade
// tickets on the same request as the complimentary tickets, allowing master users to use the
// Gratuidade name field (courtesy_name) as better way of identifying the order that needs to
// be canceled.
//
// Since this is somewhat of a specific issue and given that the structures of complimentary
// tickets order and Gratuidade tickets order are basically the same, developing a whole
// separate page, defining new permissions and API stuff to tackle such ordeal not only
// doesn't show much benefits, but would also result in significant code duplication.

const { can } = useAbility()
const snackbar = useSnackBar()
const dialog = useDialog()
const loader = useLoader()

const { isMaster } = useAdminStore()

const resource: Resources = 'complimentary-tickets'

const service = new ComplimentaryTicketService()

const datatable = useTemplateRef<DataTableType>('datatable')

const headers = computed(() => [
  { title: 'ID', value: 'id', key: 'id', sortable: false },
  { title: 'Data de Criação', value: 'created_at', key: 'created_at', sortable: false },
  { title: 'Evento', value: 'event', key: 'event', sortable: false },
  { title: 'Titular', value: 'owner', key: 'owner', sortable: false },
  { title: 'Tipo', value: 'type', key: 'type', sortable: false, align: 'center', show: () => isMaster },
  { title: 'Nome da Cortesia' + (isMaster ? ' / Gratuidade' : ''), value: 'items', key: 'courtesy_name', sortable: false },
  { title: 'Número do Pedido', value: 'order_number', key: 'order_number', sortable: false },
  { title: 'Quantidade de Ingressos', value: 'quantity', key: 'quantity', sortable: false },
  { title: 'Ações', value: 'actions', key: 'actions', sortable: false, align: 'center' },
])

const ticketTypeFilter = useSelectFilter({
  label: 'Tipo de Ingresso',
  field: 'type_name',
  items: [
    { title: 'Todos', value: '' },
    { title: 'Cortesia', value: 'Cortesia' },
    { title: 'Gratuidade', value: 'Gratuidade' },
  ],
  icon: '',
  required: false,
})

const filters = ref([
  useEventFilter({
    producer: useProducerStore().producer!,
    label: 'Evento',
    field: 'event_id',
    type: 'select',
    queryParams: {
      // It was asked to fetch only events that ended after -7 days
      end_between: Interval.fromDateTimes(
        DateTime.now().minus({ days: 7 }),
        DateTime.now().plus({ years: 5 }),
      ).toISO(),
    },
  }),
  useTextFilter({ label: 'Nome da cortesia', field: 'name', required: false }),
  useTextFilter({ label: 'Nome do titular', field: 'owner.name', required: false }),
  useTextFilter({ label: 'Documento do titular', field: 'owner.document', required: false }),
  ...(isMaster ? [ ticketTypeFilter ] : []),
])

const actions: DefaultDataTableActions<Order> = {
  print: {
    callback: async (item: Order) => service.print(item),
    condition: () => can('print', 'complimentary-tickets'),
    icon: 'fa-solid fa-print',
    color: 'gray',
  },
  destroy: {
    callback: item => destroy(item),
    condition: (item) => {
      const items = (item as Order & { items?: OrderItem[] }).items
      const origin = items?.[0].passport?.origin
      const readingExists = items?.find(item => item.passport?.reading)

      return !readingExists && (
        (origin === 'venda_online' && can('delete', 'complimentary-tickets'))
        || (origin !== 'venda_online' && can('delete', 'complimentary-tickets.pos'))
      )
    },
    icon: 'fa-solid fa-trash',
    color: 'error',
  },
}

async function fetch(page: number, _filters: string, per_page?: number) {
  // As for now, DataTable doesn't check the filter values, only the models. So once the filter is changed, it will
  // always appear on the query string regardless of the value.
  await Promise.all(filters.value.map(f => f.ready))
  const params = new URLSearchParams(
    filters.value.filter(f => f.model !== null && f.value).map(f => [f.field, f.value!]),
  )

  try {
    return await service.index(page, params.toString(), per_page)
  } catch (error) {
    snackbar.show(
      'Não foi possível buscar as cortesias.',
      'error',
    )

    throw error
  }
}

async function destroy(model: Order) {
  const confirm = await dialog.open({
    title: 'Confirmação de exclusão',
    message: 'Realmente deseja excluir a cortesia? Essa ação é irreversível',
  })

  if (!confirm) return

  try {
    loader.increment()

    await service.destroy(model)

    datatable.value?.search()

    snackbar.show(
      'Cortesia excluída com sucesso.',
      'success',
    )
  } catch {
    snackbar.show(
      'Não foi possível cancelar a cortesia.',
      'error',
    )
  } finally {
    loader.decrement()
  }
}

</script>
